import { z } from "zod";
import { addMonthsIso } from "./dossier.js";

/**
 * Onderhoudscontracten (docs/CONTRACTEN.md): een afspraak met één leverancier uit het adresboek voor één of meer
 * gebouwen van een klant. Een contract heeft posten (soort + afrekenmodel) met een prijshistorie, en een post dekt
 * uitvoeringen (onderhoud en keuringen bij assets of het gebouw).
 */

export const CONTRACT_STATUSES = ["draft", "active", "cancelled"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** Stand van een contract op een dag: volgt uit de status en de looptijd. */
export const CONTRACT_STATES = ["concept", "nog_niet", "lopend", "opgezegd", "beeindigd"] as const;
export type ContractState = (typeof CONTRACT_STATES)[number];
export const CONTRACT_STATE_LABELS: Record<ContractState, string> = {
  concept: "Concept",
  nog_niet: "Gaat nog in",
  lopend: "Lopend",
  opgezegd: "Opgezegd",
  beeindigd: "Beëindigd",
};

/** Wie het contract heeft afgesloten: de organisatie zelf of de klant (docs/CLIENTS.md §9). */
export const CONTRACTED_BY = ["organization", "client"] as const;
export type ContractedBy = (typeof CONTRACTED_BY)[number];
export const CONTRACTED_BY_LABELS: Record<ContractedBy, string> = {
  organization: "Wij (de organisatie)",
  client: "De klant",
};

export const INDEX_METHODS = ["org", "fixed", "none"] as const;
export type IndexMethod = (typeof INDEX_METHODS)[number];
export const INDEX_METHOD_LABELS: Record<IndexMethod, string> = {
  org: "Index van de organisatie (MJOP)",
  fixed: "Vast percentage per jaar",
  none: "Geen indexatie",
};

export const CONTRACT_LINE_KINDS = ["preventive", "breakdown", "other"] as const;
export type ContractLineKind = (typeof CONTRACT_LINE_KINDS)[number];
export const CONTRACT_LINE_KIND_LABELS: Record<ContractLineKind, string> = {
  preventive: "Preventief onderhoud",
  breakdown: "Storingen",
  other: "Overig",
};

/** Afrekenmodel: aangenomen (vaste prijs) of nacalculatie (uren, voorrijkosten en materiaal achteraf). */
export const CONTRACT_BILLINGS = ["fixed", "cost_plus"] as const;
export type ContractBilling = (typeof CONTRACT_BILLINGS)[number];
export const CONTRACT_BILLING_LABELS: Record<ContractBilling, string> = {
  fixed: "Aangenomen",
  cost_plus: "Nacalculatie",
};

/** Prijsvorm van een aangenomen post. */
export const PRICE_FORMS = ["period", "per_visit"] as const;
export type PriceForm = (typeof PRICE_FORMS)[number];
export const PRICE_FORM_LABELS: Record<PriceForm, string> = {
  period: "Vast bedrag per periode",
  per_visit: "Per stuk per keer",
};

export const PRICE_PERIODS = ["month", "quarter", "year"] as const;
export type PricePeriod = (typeof PRICE_PERIODS)[number];
export const PRICE_PERIOD_LABELS: Record<PricePeriod, string> = {
  month: "per maand",
  quarter: "per kwartaal",
  year: "per jaar",
};
export const PERIODS_PER_YEAR: Record<PricePeriod, number> = { month: 12, quarter: 4, year: 1 };

/** Hoe een vast bedrag per periode wordt teruggerekend naar een prijs per keer per uitvoering. */
export const ALLOCATIONS = ["weighted", "equal", "manual"] as const;
export type Allocation = (typeof ALLOCATIONS)[number];
export const ALLOCATION_LABELS: Record<Allocation, string> = {
  weighted: "Naar verhouding van de kosten per keer",
  equal: "Gelijk per keer",
  manual: "Met de hand per uitvoering",
};

export const PRICE_REASONS = ["start", "indexation", "quote", "renegotiated", "change"] as const;
export type PriceReason = (typeof PRICE_REASONS)[number];
export const PRICE_REASON_LABELS: Record<PriceReason, string> = {
  start: "Startprijs",
  indexation: "Indexatie",
  quote: "Nieuwe prijsopgave",
  renegotiated: "Heronderhandeld",
  change: "Contractwijziging",
};

/** Uitvoering die bewust niet onder een contract valt (dan geen gat in *Zonder contract*). */
export const SOURCINGS = ["own", "separate"] as const;
export type Sourcing = (typeof SOURCINGS)[number];
export const SOURCING_LABELS: Record<Sourcing, string> = {
  own: "Eigen uitvoering",
  separate: "Los inkopen",
};

/** Afkoop van storingen (C2): wat aan materiaal is inbegrepen en wanneer de leverancier komt. */
export const BUYOUT_MATERIALS = ["none", "limit", "full"] as const;
export type BuyoutMaterial = (typeof BUYOUT_MATERIALS)[number];
export const BUYOUT_MATERIAL_LABELS: Record<BuyoutMaterial, string> = {
  none: "Niet inbegrepen",
  limit: "Tot een bedrag per storing",
  full: "Helemaal inbegrepen",
};
export const SERVICE_HOURS = ["office", "always"] as const;
export type ServiceHours = (typeof SERVICE_HOURS)[number];
export const SERVICE_HOURS_LABELS: Record<ServiceHours, string> = {
  office: "Kantooruren",
  always: "24 uur per dag, 7 dagen per week",
};

/** Contracten die binnen zoveel maanden aflopen (of opgezegd moeten worden) krijgen een signaal. */
export const CONTRACT_WARN_MONTHS = 6;

// ---------- berekeningen ----------

export interface ContractTerm {
  status: ContractStatus;
  startsOn: string;
  endsOn: string | null;
  autoRenew: boolean;
  renewMonths: number | null;
  noticeMonths: number | null;
}

/**
 * Looptijd op een dag: de huidige einddatum (na stilzwijgend verlengen), de uiterste opzegdatum en de stand.
 * Zonder einddatum loopt het contract voor onbepaalde tijd; opzeggen kan dan altijd met de opzegtermijn.
 */
export function contractTerm(c: ContractTerm, today: string) {
  let currentEndOn = c.endsOn;
  if (currentEndOn && c.autoRenew && c.status !== "cancelled") {
    const step = c.renewMonths && c.renewMonths > 0 ? c.renewMonths : 12;
    // Is de opzegdatum (of zonder opzegtermijn de einddatum) voorbij, dan is de verlenging een feit en geldt de
    // volgende termijn. Hooguit 100 verlengingen: genoeg voor elke realistische looptijd.
    const lastDay = (end: string) => (c.noticeMonths ? addMonthsIso(end, -c.noticeMonths) : end);
    for (let i = 0; i < 100 && lastDay(currentEndOn) < today; i++) currentEndOn = addMonthsIso(currentEndOn, step);
  }
  const noticeBy =
    currentEndOn && c.noticeMonths != null && c.status !== "cancelled"
      ? addMonthsIso(currentEndOn, -c.noticeMonths)
      : null;
  let state: ContractState;
  if (c.status === "draft") state = "concept";
  else if (currentEndOn && currentEndOn < today) state = "beeindigd";
  else if (c.status === "cancelled") state = "opgezegd";
  else if (c.startsOn > today) state = "nog_niet";
  else state = "lopend";
  const warnBefore = addMonthsIso(today, CONTRACT_WARN_MONTHS);
  // Signaal: de opzegdatum of (bij opgezegd of zonder verlenging) de einddatum valt binnen de termijn.
  const endsSoon =
    (state === "lopend" || state === "opgezegd" || state === "nog_niet") &&
    !!currentEndOn &&
    ((noticeBy !== null && noticeBy <= warnBefore) ||
      ((c.status === "cancelled" || !c.autoRenew) && currentEndOn <= warnBefore));
  return { currentEndOn, noticeBy, state, endsSoon };
}

/** Telt het contract mee voor dekking (concept en beëindigd niet). */
export const coversWork = (state: ContractState) => state === "lopend" || state === "opgezegd" || state === "nog_niet";

/** Aantal keren per jaar van een uitvoering; eenmalig (geen interval) telt niet. */
export function visitsPerYear(o: { intervalMonths: number | null; intervalWeeks: number | null }): number {
  if (o.intervalWeeks) return 52 / o.intervalWeeks;
  if (o.intervalMonths) return 12 / o.intervalMonths;
  return 0;
}

/** Prijs die op een dag geldt: de laatste met `validFrom` op of voor die dag. */
export function priceOn<T extends { validFrom: string }>(prices: T[], day: string): T | null {
  let best: T | null = null;
  for (const p of prices) if (p.validFrom <= day && (!best || p.validFrom > best.validFrom)) best = p;
  return best;
}

/** Bedrag per jaar van een aangenomen post met een vast bedrag per periode. */
export const yearCents = (amountCents: number, period: PricePeriod) => amountCents * PERIODS_PER_YEAR[period];

/**
 * Vast bedrag per jaar teruggerekend naar een indicatieve prijs per keer per uitvoering. *Naar verhouding* gebruikt
 * de kosten per keer die al bij de uitvoeringen staan (valt terug op gelijk als die ontbreken); *met de hand* neemt
 * het ingevulde aandeel over. Uitvoeringen zonder interval krijgen geen aandeel.
 */
export function allocateShares(
  totalYearCents: number,
  allocation: Allocation,
  obligations: {
    id: string;
    intervalMonths: number | null;
    intervalWeeks: number | null;
    costCents: number | null;
    manualShareCents: number | null;
  }[],
): Map<string, number | null> {
  const out = new Map<string, number | null>();
  const recurring = obligations.filter((o) => visitsPerYear(o) > 0);
  for (const o of obligations) out.set(o.id, null);
  if (allocation === "manual") {
    for (const o of recurring) out.set(o.id, o.manualShareCents);
    return out;
  }
  const weighted = allocation === "weighted" && recurring.every((o) => (o.costCents ?? 0) > 0);
  const weight = (o: (typeof recurring)[number]) => (weighted ? o.costCents! : 1) * visitsPerYear(o);
  const sum = recurring.reduce((n, o) => n + weight(o), 0);
  if (sum <= 0) return out;
  for (const o of recurring) out.set(o.id, Math.round((totalYearCents * weight(o)) / sum / visitsPerYear(o)));
  return out;
}

/** Wijziging in procenten ten opzichte van de vorige prijs, op één decimaal; null zonder vergelijking. */
export function changePercent(prev: number | null | undefined, next: number | null | undefined): number | null {
  if (prev == null || next == null || prev === 0) return null;
  return Math.round(((next - prev) / prev) * 1000) / 10;
}

// ---------- schema's ----------

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
const cents = z.number().int().min(0).max(1_000_000_000);
const months = z.number().int().min(0).max(240);

const contractFields = {
  name: z.string().trim().min(1, "Vul een naam in").max(120),
  reference: optText(60),
  clientId: z.uuid(),
  contractedBy: z.enum(CONTRACTED_BY),
  supplierCompanyId: z.uuid({ message: "Kies een leverancier uit het adresboek" }),
  supplierPersonId: z.uuid().nullish(),
  status: z.enum(CONTRACT_STATUSES),
  startsOn: z.iso.date(),
  endsOn: z.iso.date().nullish(),
  autoRenew: z.boolean(),
  renewMonths: months.nullish(),
  noticeMonths: months.nullish(),
  indexMethod: z.enum(INDEX_METHODS),
  indexPercent: z.number().min(-20).max(50).nullish(),
  responseTimes: optText(500),
  summary: optText(4000),
  documentId: z.uuid().nullish(),
};

export const contractCreateSchema = z.object({
  id: z.uuid(),
  ...contractFields,
  status: contractFields.status.default("active"),
  autoRenew: contractFields.autoRenew.default(false),
  indexMethod: contractFields.indexMethod.default("org"),
  buildingIds: z.array(z.uuid()).min(1, "Kies minstens één gebouw").max(500),
});
export type ContractCreate = z.infer<typeof contractCreateSchema>;

/** Geen defaults in de update: een veld dat ontbreekt blijft zoals het was. */
export const contractUpdateSchema = z.object(contractFields).partial();

export const contractBuildingsSchema = z.object({ buildingIds: z.array(z.uuid()).min(1).max(500) });

const priceFields = {
  validFrom: z.iso.date(),
  amountCents: cents.nullish(),
  hourlyRateCents: cents.nullish(),
  calloutCents: cents.nullish(),
  materialMarkupPercent: z.number().min(0).max(500).nullish(),
  estimateYearCents: cents.nullish(),
  reason: z.enum(PRICE_REASONS),
  note: optText(500),
};
export const contractPriceSchema = z.object({ id: z.uuid(), ...priceFields });
export type ContractPriceInput = z.infer<typeof contractPriceSchema>;

const lineFields = {
  description: z.string().trim().min(1, "Vul een omschrijving in").max(200),
  kind: z.enum(CONTRACT_LINE_KINDS),
  billing: z.enum(CONTRACT_BILLINGS),
  priceForm: z.enum(PRICE_FORMS).nullish(),
  period: z.enum(PRICE_PERIODS).nullish(),
  allocation: z.enum(ALLOCATIONS),
  buildingId: z.uuid().nullish(),
  notes: optText(1000),
  includesLabour: z.boolean(),
  includesCallout: z.boolean(),
  materialMode: z.enum(BUYOUT_MATERIALS),
  materialLimitCents: cents.nullish(),
  maxCallsPerYear: z.number().int().min(1).max(10_000).nullish(),
  serviceHours: z.enum(SERVICE_HOURS),
  responseHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 60)
    .nullish(),
};
export const contractLineCreateSchema = z.object({
  id: z.uuid(),
  ...lineFields,
  allocation: lineFields.allocation.default("weighted"),
  includesLabour: lineFields.includesLabour.default(true),
  includesCallout: lineFields.includesCallout.default(true),
  materialMode: lineFields.materialMode.default("none"),
  serviceHours: lineFields.serviceHours.default("office"),
  /** Startprijs; `validFrom` leeg = ingangsdatum van het contract. */
  price: contractPriceSchema
    .extend({ validFrom: z.iso.date().nullish(), reason: z.enum(PRICE_REASONS).default("start") })
    .nullish(),
});
export const contractLineUpdateSchema = z.object(lineFields).partial();

/** Uitvoeringen onder een post brengen of eruit halen. */
export const contractLineObligationsSchema = z.object({
  add: z.array(z.uuid()).max(1000).default([]),
  remove: z.array(z.uuid()).max(1000).default([]),
});

/** Assets waarvan de storingen onder een storingspost vallen (afkoop). */
export const contractLineAssetsSchema = z.object({
  add: z.array(z.uuid()).max(1000).default([]),
  remove: z.array(z.uuid()).max(1000).default([]),
});

/** Aandeel per keer met de hand (prijsvorm vast bedrag, verdeling met de hand). */
export const contractShareSchema = z.object({ shareCents: cents.nullable() });

/** Uitvoeringen markeren als eigen uitvoering of los inkopen (of de markering weghalen). */
export const sourcingSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(1000),
  sourcing: z.enum(SOURCINGS).nullable(),
});

// ---------- DTO's ----------

export interface ContractRef {
  id: string;
  name: string;
}

export interface ContractPriceDto {
  id: string;
  validFrom: string;
  amountCents: number | null;
  hourlyRateCents: number | null;
  calloutCents: number | null;
  materialMarkupPercent: number | null;
  estimateYearCents: number | null;
  reason: PriceReason;
  note: string | null;
  /** Wijziging ten opzichte van de vorige prijs (bedrag of uurtarief), in %. */
  changePercent: number | null;
  createdBy: string | null;
  createdAt: string;
}

export interface CoveredObligationDto {
  id: string;
  name: string;
  kind: string;
  basis: string;
  intervalMonths: number | null;
  intervalWeeks: number | null;
  nextDueOn: string | null;
  building: { id: string; code: string; name: string };
  asset: { id: string; code: string; name: string } | null;
  /** Kosten per keer bij de uitvoering (null zonder MJOP-recht). */
  costCents: number | null;
  /** Indicatieve prijs per keer uit het vaste bedrag van de post. */
  shareCents: number | null;
  /** Met de hand ingevuld aandeel per keer. */
  manualShareCents: number | null;
}

export interface ContractLineDto {
  id: string;
  description: string;
  kind: ContractLineKind;
  billing: ContractBilling;
  priceForm: PriceForm | null;
  period: PricePeriod | null;
  allocation: Allocation;
  building: { id: string; code: string; name: string } | null;
  notes: string | null;
  /** Prijs die vandaag geldt; null als er nog geen is (of alleen een toekomstige). */
  price: ContractPriceDto | null;
  /** Alle prijzen, nieuwste eerst. */
  prices: ContractPriceDto[];
  /** Vast bedrag per jaar (aangenomen, per periode); bij per stuk per keer: prijs × keren per jaar. */
  yearCents: number | null;
  obligations: CoveredObligationDto[];
  /** Storingen: afkoopvoorwaarden en gedekte assets. */
  includesLabour: boolean;
  includesCallout: boolean;
  materialMode: BuyoutMaterial;
  materialLimitCents: number | null;
  maxCallsPerYear: number | null;
  serviceHours: ServiceHours;
  responseHours: number | null;
  assets: CoveredAssetDto[];
  /** Storingen (taken) onder deze post in het lopende kalenderjaar. */
  callsThisYear: number;
}

/** Asset onder een storingspost. */
export interface CoveredAssetDto {
  id: string;
  code: string;
  name: string;
  building: { id: string; code: string; name: string };
}

/** Storing (taak) onder een storingspost, tabblad *Storingen*. */
export interface ContractCallDto {
  id: string;
  code: string;
  title: string;
  status: string;
  createdAt: string;
  lineId: string;
  building: { id: string; code: string; name: string };
  /** Volgnummer in het kalenderjaar van de post; boven het maximum valt buiten de afkoop. */
  nr: number;
  overMax: boolean;
  /** Eerste keer *Bezig* of *Klaar*; leeg zolang de storing nog open staat. */
  startedAt: string | null;
  /** Minuten van melden tot opgepakt (bij een open storing: tot nu). */
  responseMinutes: number;
  /** Binnen de reactietijd van de post; leeg zonder reactietijd of zolang een open storing nog binnen de tijd zit. */
  onTime: boolean | null;
}

/** Afkoop bij een asset (Inbox, taak): de storingspost die de asset dekt. */
export interface BuyoutInfoDto {
  contractId: string;
  contractName: string;
  lineId: string;
  lineDescription: string;
  billing: ContractBilling;
  supplier: { id: string; name: string; emergencyPhone: string | null } | null;
  serviceHours: ServiceHours;
  responseHours: number | null;
  maxCallsPerYear: number | null;
  callsThisYear: number;
  /** Korte omschrijving van wat inbegrepen is. */
  includes: string;
}

export interface ContractSummaryDto {
  id: string;
  name: string;
  reference: string | null;
  client: { id: string; name: string };
  contractedBy: ContractedBy;
  supplier: { id: string; name: string; emergencyPhone: string | null } | null;
  person: { id: string; name: string } | null;
  status: ContractStatus;
  state: ContractState;
  startsOn: string;
  endsOn: string | null;
  /** Einddatum na stilzwijgend verlengen. */
  currentEndOn: string | null;
  noticeBy: string | null;
  endsSoon: boolean;
  autoRenew: boolean;
  renewMonths: number | null;
  noticeMonths: number | null;
  buildings: { id: string; code: string; name: string }[];
  lineCount: number;
  obligationCount: number;
  /** Som van de posten per jaar (bekende bedragen). */
  yearCents: number | null;
  /** Bedragen weggelaten: de klant ziet ze alleen als hij zelf contractant is (docs/CONTRACTEN.md, C4). */
  amountsHidden: boolean;
}

export interface ContractDetailDto extends ContractSummaryDto {
  indexMethod: IndexMethod;
  indexPercent: number | null;
  /** Index waarmee prijzen doorgerekend worden: eigen percentage, die van de organisatie (MJOP) of 0. */
  effectiveIndexPercent: number;
  responseTimes: string | null;
  summary: string | null;
  document: { id: string; title: string; buildingId: string } | null;
  lines: ContractLineDto[];
  /** Contractdocumenten in het dossier van de gebouwen, om te koppelen. */
  documents: { id: string; title: string; buildingId: string }[];
}

/** Uitvoering die onder geen lopend contract valt (overzicht *Zonder contract*). */
export interface UncoveredObligationDto {
  id: string;
  name: string;
  kind: string;
  basis: string;
  intervalMonths: number | null;
  intervalWeeks: number | null;
  nextDueOn: string | null;
  performer: string | null;
  building: { id: string; code: string; name: string };
  client: { id: string; name: string };
  asset: { id: string; code: string; name: string } | null;
  /** Kosten per jaar (kosten per keer × keren per jaar); null zonder MJOP-recht of bedrag. */
  yearCostCents: number | null;
  /** `none`: nooit gekoppeld; `ended`: het contract is afgelopen; anders de markering. */
  reason: "none" | "ended" | Sourcing;
  /** Afgelopen contract (bij `ended`). */
  endedContract: ContractRef | null;
}

/** Contract bij een uitvoering (paneel van de asset en de keuringenlijst). */
/** Ziet deze gebruiker de bedragen van het contract? Een klant alleen als hij zelf contractant is. */
export const contractAmountsHidden = (role: string, contractedBy: ContractedBy) =>
  role === "klant" && contractedBy !== "client";

export interface ObligationContractDto {
  contractId: string;
  contractName: string;
  lineId: string;
  lineDescription: string;
  billing: ContractBilling;
  priceForm: PriceForm | null;
  supplierName: string | null;
  state: ContractState;
  /** Indicatief aandeel per keer bij een vast bedrag per periode. */
  shareCents: number | null;
}

/** Korte tekst van wat een storingspost dekt, bijvoorbeeld "arbeid, voorrijden, materiaal tot € 50". */
export function buyoutIncludes(
  l: {
    includesLabour: boolean;
    includesCallout: boolean;
    materialMode: BuyoutMaterial;
    materialLimitCents: number | null;
  },
  euro: (cents: number) => string,
): string {
  const parts: string[] = [];
  if (l.includesLabour) parts.push("arbeid");
  if (l.includesCallout) parts.push("voorrijden");
  if (l.materialMode === "full") parts.push("materiaal");
  else if (l.materialMode === "limit")
    parts.push(
      l.materialLimitCents != null ? `materiaal tot ${euro(l.materialLimitCents)}` : "materiaal tot een bedrag",
    );
  return parts.length ? parts.join(", ") : "niets";
}
